import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Button } from '@material-ui/core';
import { useLeaflet } from 'react-leaflet';
import { useDirectoryBusinessOptions } from '../../../hooks/useDriverDirectory/useDirectoryBusinessOptions';
import { useTerritoryCoverage } from '../../../hooks/useTerritoryCoverage';
import { canViewCoverage, rankCoverage } from '../../../services/TerritoryCoverage';
import { createDisplayAreaCache } from '../../../services/TerritoryDisplayGeometry';
import { CoverageLayer } from './CoverageLayer';
import { CoveragePanel } from './CoveragePanel';
import { useStyles } from './Coverage.styles';
import { MapControl } from '../MapControls/MapControls';
import { useMapControls } from '../MapControls/MapControlsContext';

export { canViewCoverage } from '../../../services/TerritoryCoverage';

// Mount inside react-leaflet Map. The panel is a sibling of its native container:
// Leaflet's native stopPropagation otherwise swallows React 16's delegated
// events (including MUI select menus). Only polygons live inside the native map.
export function CoverageController() {
  const { map } = useLeaflet();
  const controls = useMapControls();
  const classes = useStyles();
  const [bounds, setBounds] = useState(null);
  const options = useDirectoryBusinessOptions();
  const [enabled, setEnabled] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [choice, setChoice] = useState({ id: '', revision: null });
  const [ratioState, setRatioState] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const businessId = options.status === 'success' &&
    options.revision === choice.revision &&
    Number.isSafeInteger(Number(choice.id)) && Number(choice.id) > 0 &&
    options.data.some((option) => String(option.id) === choice.id) ? choice.id : '';
  const active = enabled && !!businessId;
  const coverage = useTerritoryCoverage(active ? Number(businessId) : null, active);
  const unauthorized = coverage.error?.status === 403 || coverage.error?.response?.status === 403;
  // Do not allow stale cross-scope snapshots to leak even during a hook transition.
  const data = active && !unauthorized ? coverage.data : null;
  // Discard all derived geometry on scope/authorization/layer changes.
  const displayArea = useMemo(
    () => active && businessId && !unauthorized ? createDisplayAreaCache() : () => null,
    [businessId, active, unauthorized]
  );
  const features = useMemo(
    () => data ? rankCoverage(data.features, ratioState).map((feature) => ({
      ...feature, displayArea: displayArea(feature.geometry),
    })) : [],
    [data, ratioState, displayArea]
  );
  useEffect(() => {
    if (!map || controls) return undefined;
    const element = map.getContainer();
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setBounds((previous) => previous &&
        previous.top === rect.top && previous.left === rect.left &&
        previous.width === rect.width && previous.height === rect.height ? previous :
        { top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(element);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      if (observer) observer.disconnect();
    };
    // The shared frame measures the map when integrated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, Boolean(controls)]);
  const chooseBusiness = (id) => {
    setSelectedId(null);
    setChoice({ id, revision: options.revision });
  };
  const panel = (
    <CoveragePanel
      embedded={!!controls}
      collapsed={controls ? false : collapsed}
      onCollapsed={controls ? controls.close : setCollapsed}
      enabled={enabled}
      onEnabled={(value) => { setEnabled(value); setSelectedId(null); }}
      options={options} businessId={businessId} onBusiness={chooseBusiness}
      data={data} error={active ? coverage.error : null}
      isFetching={active && coverage.isFetching}
      refetch={coverage.refetch} now={coverage.now} stale={coverage.stale}
      features={features} ratioState={ratioState} onRatioState={setRatioState}
      selectedId={selectedId} onSelect={setSelectedId}
    />
  );
  return (
    <>
      {map && data && (
        <CoverageLayer
          key={map._leaflet_id}
          features={features} snapshot={`${businessId}:${data.as_of_utc}`}
          selectedId={selectedId} onSelect={setSelectedId}
        />
      )}
      {controls && <MapControl id="coverage" launcher={
        <Button size="small" aria-expanded={controls.active === 'coverage'}
          aria-controls="map-control-coverage"
          ref={(element) => { controls.launchers.current.coverage = element; }}
          onClick={() => controls.setActive(controls.active === 'coverage' ? null : 'coverage')}>
          Coverage
        </Button>
      }>{panel}</MapControl>}
      {!controls && map && bounds && createPortal(
        <div className={classes.overlay} style={bounds}>
          {panel}
        </div>, map.getContainer().parentNode
      )}
    </>
  );
}

export default function Coverage() {
  const user = useSelector((state) => state.user.user);
  return canViewCoverage(user) ? <CoverageController key={user.id} /> : null;
}
